import { type FC, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Button } from './ui/button';
import { Plus } from 'lucide-react';
import { FilterRow } from './FilterRow';
import type { ComboboxOption } from './ui/combobox';
import type { ComparatorOption } from '../utils/filterComparators';
import { comparatorOptions as defaultComparatorOptions } from '../utils/filterComparators';
import { useIsReadOnly } from '../contexts/ReadOnlyContext';

export interface FilterEntry {
  columnId: string;
  comparator: string;
  value?: string | string[];
}

export interface FilterTableProps {
  /** Array of filter entries */
  filters: FilterEntry[];
  /** Callback when filters change */
  onChange: (filters: FilterEntry[]) => void;
  /** Options for the column/dimension selector */
  columnOptions: ComboboxOption[];
  /** Label for the column header (default: "Column") */
  columnLabel?: string;
  /** Whether to show the comparator column (default: true) */
  showComparator?: boolean;
  /** Options for the comparator dropdown */
  comparatorOptions?: ComparatorOption[];
  /** Default comparator for new filters (default: "EQUALS") */
  defaultComparator?: string;
  /** Default value for new filters */
  defaultValue?: string | string[];
  /** Message shown when no filters are added */
  emptyMessage?: string;
  /** Value input mode: "text" for simple text input, "multiselect" for multi-select combobox */
  valueMode?: 'text' | 'multiselect';
  /** Callback to fetch value options for multiselect mode */
  onFetchValueOptions?: (columnId: string, query: string) => Promise<string[]>;
  /** Whether value options are currently loading */
  isLoadingValues?: boolean;
  /** Currently loaded value options (managed externally) */
  valueOptions?: { value: string; label: string }[];
  /** Callback when search query changes (for external value fetching) */
  onValueSearchChange?: (columnId: string, query: string) => void;
  /** Function to determine if a filter row should be read-only */
  isReadOnly?: (filter: FilterEntry) => boolean;
  /** Function to get column label from columnId */
  getColumnLabel?: (columnId: string) => string | undefined;
}

export const FilterTable: FC<FilterTableProps> = ({
  filters,
  onChange,
  columnOptions,
  columnLabel = 'Column',
  showComparator = true,
  comparatorOptions = defaultComparatorOptions,
  defaultComparator = 'EQUALS',
  defaultValue = '',
  emptyMessage = 'No filters added. Click the + button to add a filter.',
  valueMode = 'text',
  onFetchValueOptions,
  isLoadingValues = false,
  valueOptions = [],
  onValueSearchChange,
  isReadOnly,
  getColumnLabel,
}) => {
  const inReadOnlyRegion = useIsReadOnly();
  const handleAddFilter = useCallback(() => {
    onChange([
      ...filters,
      {
        columnId: '',
        comparator: defaultComparator,
        value: defaultValue,
      },
    ]);
  }, [filters, onChange, defaultComparator, defaultValue]);

  const handleDelete = useCallback(
    (idx: number) => {
      const newFilters = [...filters];
      newFilters.splice(idx, 1);
      onChange(newFilters);
    },
    [filters, onChange]
  );

  const handleFilterChange = useCallback(
    (idx: number, newFilter: FilterEntry) => {
      const newFilters = [...filters];
      newFilters[idx] = newFilter;
      onChange(newFilters);
    },
    [filters, onChange]
  );

  // Calculate column widths based on whether comparator is shown
  const columnWidth = showComparator ? 'w-[30%]' : 'w-[40%]';
  const valueWidth = showComparator ? 'w-[35%]' : 'w-[50%]';
  const comparatorWidth = 'w-[25%]';
  const actionWidth = 'w-[10%]';
  const colSpan = showComparator ? 4 : 3;

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className={columnWidth}>{columnLabel}</TableHead>
            {showComparator && (
              <TableHead className={comparatorWidth}>Comparator</TableHead>
            )}
            <TableHead className={valueWidth}>Value</TableHead>
            <TableHead className={`${actionWidth} text-right`}>
              {!inReadOnlyRegion && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAddFilter}
                  className="h-8 w-8 p-0"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filters.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan} className="text-center text-muted-foreground py-8">
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            filters.map((filter, idx) => (
              <FilterRow
                key={idx}
                filter={filter}
                onChange={(newFilter) => handleFilterChange(idx, newFilter)}
                onDelete={() => handleDelete(idx)}
                columnOptions={columnOptions}
                showComparator={showComparator}
                comparatorOptions={comparatorOptions}
                defaultComparator={defaultComparator}
                valueMode={valueMode}
                onFetchValueOptions={onFetchValueOptions}
                isLoadingValues={isLoadingValues}
                valueOptions={valueOptions}
                onValueSearchChange={onValueSearchChange}
                isReadOnly={inReadOnlyRegion || isReadOnly?.(filter)}
                getColumnLabel={getColumnLabel}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
